import React, { useState } from 'react';
import { PageSection, FAQSectionContent, FAQItem } from '../../types/page';
import { ChevronDown, HelpCircle } from 'lucide-react';

interface FAQSectionProps {
  section: PageSection;
  className?: string;
}

export const FAQSection: React.FC<FAQSectionProps> = ({
  section,
  className = ''
}) => {
  const content = section.content as FAQSectionContent;
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  // Default FAQ items if none provided
  const defaultFAQs: FAQItem[] = [
    {
      question: "¿Cómo funciona la plataforma?",
      answer: "Nuestra plataforma le permite buscar, comparar y gestionar propiedades de manera sencilla. Puede explorar nuestro catálogo completo, filtrar por sus preferencias, y contactarnos directamente para obtener más información sobre cualquier propiedad."
    },
    {
      question: "¿Qué comisiones cobran?",
      answer: "Nuestras comisiones son competitivas y transparentes. Varían según el tipo de transacción (compra, venta o arriendo). Contáctenos para obtener información detallada sobre nuestras tarifas y descubrir cómo podemos ayudarle a ahorrar en su próxima transacción inmobiliaria."
    },
    {
      question: "¿Cuánto tiempo toma publicar una propiedad?",
      answer: "Una vez que recibimos toda la información y documentación necesaria, su propiedad puede estar publicada en nuestra plataforma en menos de 24 horas. Nuestro equipo se encarga de crear una presentación profesional con fotos de alta calidad y una descripción detallada."
    },
    {
      question: "¿Ofrecen soporte técnico?",
      answer: "Sí, contamos con un equipo de soporte disponible para ayudarle con cualquier duda o problema técnico. Puede contactarnos por teléfono, correo electrónico o chat en vivo durante nuestro horario de atención. También ofrecemos una base de conocimientos completa para consultas rápidas."
    },
    {
      question: "¿Puedo agendar visitas a las propiedades?",
      answer: "Absolutamente. Puede agendar visitas directamente desde nuestra plataforma o contactando a nuestros agentes. Ofrecemos horarios flexibles para adaptarnos a su disponibilidad y también realizamos visitas virtuales para su comodidad."
    },
    {
      question: "¿Qué documentación necesito para comprar o arrendar?",
      answer: "Los requisitos varían según el tipo de transacción. Para compra, generalmente necesitará identificación, comprobantes de ingresos y referencias financieras. Para arriendo, además de lo anterior, solicitamos referencias personales y laborales. Nuestros asesores le guiarán en todo el proceso."
    }
  ];

  const faqItems = content.items && content.items.length > 0 ? content.items : defaultFAQs;

  const backgroundStyle = content.backgroundImage
    ? { backgroundImage: `url(${content.backgroundImage})` }
    : {};

  const toggleFAQ = (index: number) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const handleKeyDown = (event: React.KeyboardEvent, index: number) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleFAQ(index);
    }
  };

  return (
    <section
      className={`relative py-20 bg-cover bg-center bg-no-repeat ${className}`}
      style={backgroundStyle}
      aria-labelledby="faq-section-title"
    >
      {/* Overlay */}
      {content.backgroundImage && (
        <div className="absolute inset-0 bg-black/70" aria-hidden="true" />
      )}

      <div className="relative z-10 container mx-auto px-4 max-w-4xl">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 text-blue-600 mb-6">
            <HelpCircle className="w-8 h-8" aria-hidden="true" />
          </div>

          <h2
            id="faq-section-title"
            className={`text-3xl md:text-4xl font-bold mb-4 ${
              content.backgroundImage ? 'text-white' : 'text-gray-900'
            }`}
          >
            {section.title || content.title || 'Preguntas Frecuentes'}
          </h2>

          {section.subtitle && (
            <p className={`text-xl max-w-2xl mx-auto ${
              content.backgroundImage ? 'text-gray-200' : 'text-gray-600'
            }`}>
              {section.subtitle}
            </p>
          )}
        </div>

        {/* FAQ Accordion */}
        <div className="space-y-4">
          {faqItems.map((faq, index) => {
            const isActive = activeIndex === index;

            return (
              <div
                key={index}
                className={`rounded-xl overflow-hidden transition-all duration-200 ${
                  content.backgroundImage
                    ? 'bg-white/10 backdrop-blur-sm border border-white/20'
                    : 'bg-white shadow-lg border border-gray-100 hover:shadow-xl'
                }`}
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  className={`w-full flex items-center justify-between p-6 text-left transition-colors ${
                    content.backgroundImage
                      ? 'hover:bg-white/5 focus:bg-white/5'
                      : 'hover:bg-gray-50 focus:bg-gray-50'
                  } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset`}
                  aria-expanded={isActive}
                  aria-controls={`faq-answer-${index}`}
                  id={`faq-question-${index}`}
                >
                  <h3 className={`text-lg font-semibold pr-8 ${
                    content.backgroundImage ? 'text-white' : 'text-gray-900'
                  }`}>
                    {faq.question}
                  </h3>

                  <ChevronDown
                    className={`w-5 h-5 flex-shrink-0 transition-transform duration-200 ${
                      content.backgroundImage ? 'text-white' : 'text-gray-500'
                    } ${isActive ? 'transform rotate-180' : ''}`}
                    aria-hidden="true"
                  />
                </button>

                <div
                  id={`faq-answer-${index}`}
                  role="region"
                  aria-labelledby={`faq-question-${index}`}
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    isActive ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className={`p-6 pt-0 ${
                    content.backgroundImage ? 'text-gray-200' : 'text-gray-700'
                  }`}>
                    <p className="text-base leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer Note */}
        <div className="mt-12 text-center">
          <p className={`text-sm ${
            content.backgroundImage ? 'text-gray-300' : 'text-gray-600'
          }`}>
            ¿No encuentra la respuesta que busca?{' '}
            <a
              href="/contacto"
              className={`font-semibold underline hover:no-underline ${
                content.backgroundImage ? 'text-white' : 'text-blue-600 hover:text-blue-700'
              }`}
              aria-label="Ir a la página de contacto para más información"
            >
              Contáctenos
            </a>
            {' '}y con gusto le ayudaremos.
          </p>
        </div>
      </div>
    </section>
  );
};
